import { useLocation } from "react-router-dom";
import { message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getFootnoteData,
  getFootnoteDropdownOptionsList,
  getFootnoteTypeList,
  getSoaActivityLib,
  insertFootnoteDetail,
  updateFootnoteDetail,
  deleteFootnoteDetail,
} from "../utils/ajax-proxy";
import {
  updateDPFootnoteConfigData,
  updateDPFootnoteDataList,
  updateDPFootnoteSoftSave,
} from "../actions/createDP";
import { fetchHistory } from "src/actions/historyTrial";
import {
  ActivityLibraryType,
  SoaDetailsState,
} from "../pages/DpAddActivitySoa/types";
import { uniqueArray } from "src/utils/common-function";
import { FOOTNOTE_GROUP, SOFT_SAVE_ID_PREFIX } from "../constants";

export function useFootnoteHooks() {
  const dispatch = useDispatch();
  const location = useLocation();
  const locationState = location.state as SoaDetailsState;

  // get footnote config
  const footnoteConfigState = useSelector(fetchHistory).data?.dpFootnoteConfig;
  // get footnote hard saved data
  const footnoteDataState = useSelector(fetchHistory).data.dpFootnoteData;
  // get footnote soft saved data
  const footnoteSoftSaveState =
    useSelector(fetchHistory).data.dpFootnoteSoftSave;
  // get SoA builder table Data
  const dpSoaState = useSelector(fetchHistory).data?.dpSoaReducer;

  const covertWeekAndDayForContinuousMonitor = (
    data: {
      continuously_monitored_from_number_text: number;
      continuously_monitored_to_number_text: number;
      footnotePreivew: string;
      previewPre: string;
      day_week_visit_type: string;
    },
    param: {
      option_id_for_from_day: number;
      option_id_for_from_week: number;
      option_id_for_to_day: number;
      option_id_for_to_week: number;
    },
    unit: string
  ) => {
    let {
      continuously_monitored_from_number_text,
      continuously_monitored_to_number_text,
      footnotePreivew,
      previewPre,
      day_week_visit_type,
    } = data;
    const newType = day_week_visit_type === "Week" ? "Day" : "Week";
    let new_continuously_monitored_from_number_text = 0;
    let new_continuously_monitored_to_number_text = 0;
    if (day_week_visit_type === "Week") {
      new_continuously_monitored_from_number_text =
        continuously_monitored_from_number_text * 7;
      new_continuously_monitored_to_number_text =
        continuously_monitored_to_number_text * 7;
    } else {
      new_continuously_monitored_from_number_text = Math.round(
        continuously_monitored_from_number_text / 7
      );
      new_continuously_monitored_to_number_text = Math.round(
        continuously_monitored_to_number_text / 7
      );
    }

    return {
      ...data,
      day_week_visit_type: unit,
      continuously_monitored_from_select:
        param[`option_id_for_from_${newType.toLowerCase()}`],
      continuously_monitored_to_select:
        param[`option_id_for_to_${newType.toLowerCase()}`],
      continuously_monitored_from_number_text:
        new_continuously_monitored_from_number_text,
      continuously_monitored_to_number_text:
        new_continuously_monitored_to_number_text,
      footnotePreivew: footnotePreivew
        ? footnotePreivew
            .replaceAll(
              `${day_week_visit_type} ${continuously_monitored_from_number_text}`,
              `${newType} ${new_continuously_monitored_from_number_text}`
            )
            .replaceAll(
              `${day_week_visit_type} ${continuously_monitored_to_number_text}`,
              `${newType} ${new_continuously_monitored_to_number_text}`
            )
        : footnotePreivew,
      previewPre: previewPre
        ? previewPre
            .replaceAll(
              `${day_week_visit_type} ${continuously_monitored_from_number_text}`,
              `${newType} ${new_continuously_monitored_from_number_text}`
            )
            .replaceAll(
              `${day_week_visit_type} ${continuously_monitored_to_number_text}`,
              `${newType} ${new_continuously_monitored_to_number_text}`
            )
        : previewPre,
    };
  };

  // fetch footnote type list and dropdown option list
  const fetchFootnoteConfig = (trial_uuid: string) => {
    Promise.allSettled([
      getFootnoteTypeList(),
      getFootnoteDropdownOptionsList(),
      getSoaActivityLib(trial_uuid),
    ])
      .then(([typesRes, optionsRes, activityRes]) => {
        let tmpFootnoteTypeAllList: FootnoteConfig.FootnoteType[] = [];
        let tmpFootnoteOptions: FootnoteConfig.Options[] = [];
        let temActivityListAll: ActivityLibraryType[] = [];
        // footnote type
        if (typesRes.status === "fulfilled") {
          tmpFootnoteTypeAllList = typesRes.value;
        }

        // footnote dropdown options
        if (optionsRes.status === "fulfilled") {
          tmpFootnoteOptions = optionsRes.value?.footnote_dropdown_list || [];
        }

        // all activities
        if (activityRes.status === "fulfilled") {
          const res = activityRes.value;
          temActivityListAll = [
            ...res?.["Labs"],
            ...res?.["Physical Examination"],
            ...res?.["Procedures"],
            ...res?.["Questionnaires"],
            ...res?.["Study Procedures"],
          ];
        }

        dispatch(
          updateDPFootnoteConfigData({
            footnoteTypeList: tmpFootnoteTypeAllList,
            footnoteOptionList: tmpFootnoteOptions,
            activityOptionList: temActivityListAll,
          })
        );
      })
      .catch((e) => {
        message.error("Failed to fetch footnote configuration data");
      })
      .finally(() => {});
  };

  // fetch all footnote records
  const fetchAllFootnoteData = (
    trial_uuid: string,
    table_id: number,
    unit: string,
    soaTableData: Digital_Protocol_SoA_Table_Data.DP_Scenario | null,
    setLoading?: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setLoading && setLoading(true);
    getFootnoteData(trial_uuid, table_id)
      .then((res) => {
        if (res && res.execution_status.includes("success")) {
          const footnoteDataList = res.footnote_summary_list.map((item) => {
            if (item) {
              const data = JSON.parse(item.footnote_details_json);
              if (
                data.day_week_visit_type &&
                data.day_week_visit_type !== "Visit" &&
                data.day_week_visit_type !== unit
              ) {
                const convertedData = covertWeekAndDayForContinuousMonitor(
                  data,
                  {
                    option_id_for_from_day:
                      data.continuously_monitored_the_option_id_for_from_day,
                    option_id_for_from_week:
                      data.continuously_monitored_the_option_id_for_from_week,
                    option_id_for_to_day:
                      data.continuously_monitored_the_option_id_for_to_day,
                    option_id_for_to_week:
                      data.continuously_monitored_the_option_id_for_to_week,
                  },
                  unit
                );
                return {
                  ...item,
                  footnote_details_json: JSON.stringify(convertedData),
                  footnote_preview_text: convertedData.footnotePreivew,
                };
              } else {
                return item;
              }
            } else {
              return item;
            }
          });
          // do footnote validation for warning message on page
          const footnoteDataListWithWarning = doValidationForWarning(
            soaTableData,
            footnoteDataList
          );
          dispatch(
            updateDPFootnoteDataList({
              footnoteDataList: footnoteDataListWithWarning,
            })
          );
        }
      })
      .catch((e) => {
        message.error("Failed to fetch footnote data");
      })
      .finally(() => {
        setLoading && setLoading(false);
      });
  };

  // filter all the dropdown options by footnote type
  const filterDropdownOptionsByFootnoteType = (
    currentFootnoteTypeId: number
  ) => {
    const footnoteOptionListState =
      footnoteConfigState?.footnoteOptionList || [];
    return (
      footnoteOptionListState.filter(
        (item) => item.footnote_type_id === currentFootnoteTypeId
      ) || []
    );
  };

  // get the footnote data combined with hard saved and soft saved data
  const getFootnoteStateMergedWithSoftSave = () => {
    const mergedFootnoteDataStatus = uniqueArray(
      [
        ...footnoteSoftSaveState.insert,
        ...footnoteSoftSaveState.update,
        ...footnoteDataState.footnoteDataList,
      ],
      "footnote_id"
    ).filter(
      (item: DPFootnote.PayloadType) =>
        !footnoteSoftSaveState.delete.includes(item.footnote_id)
    );
    return mergedFootnoteDataStatus;
  };

  /**
   * Soft delete footnote to redux
   *
   * @param: payload
   * payload type is any[]: delete hard/soft saved data
   */
  const doFootnoteSoftDelete = (payload: (number | string)[]) => {
    let tmpFootnoteSoftSaveState = JSON.parse(
      JSON.stringify(footnoteSoftSaveState)
    );
    payload.forEach((footnoteId: any) => {
      if (typeof footnoteId === "number") {
        // remove hard saved data
        const update = tmpFootnoteSoftSaveState.update.filter(
          (item: DPFootnote.PayloadType) => item.footnote_id !== footnoteId
        );
        tmpFootnoteSoftSaveState = {
          ...tmpFootnoteSoftSaveState,
          update,
          delete: Array.from(
            new Set([...tmpFootnoteSoftSaveState.delete, footnoteId])
          ),
        };
      } else if (typeof footnoteId === "string") {
        // remove soft saved data
        const insert = tmpFootnoteSoftSaveState.insert.filter(
          (item: DPFootnote.PayloadType) => item.footnote_id !== footnoteId
        );
        tmpFootnoteSoftSaveState = {
          ...tmpFootnoteSoftSaveState,
          insert,
        };
      }
    });
    dispatch(updateDPFootnoteSoftSave(tmpFootnoteSoftSaveState));
  };

  /**
   * Soft insert or update footnote to redux
   *
   * @param: payload
   * payload type is DPFootnote.PayloadType[]
   */
  const doFootnoteSoftUpsert = (payload: DPFootnote.PayloadType[]) => {
    let insert: DPFootnote.PayloadType[] = footnoteSoftSaveState.insert;
    let update: DPFootnote.PayloadType[] = footnoteSoftSaveState.update;
    payload.forEach((item) => {
      const isInsert = typeof item.footnote_id === "string";
      if (isInsert) {
        insert = uniqueArray([item, ...insert], "footnote_id");
      } else {
        update = uniqueArray([item, ...update], "footnote_id");
      }
    });
    dispatch(
      updateDPFootnoteSoftSave({
        ...footnoteSoftSaveState,
        insert,
        update,
      })
    );
  };

  const doFootnoteHardSave = async (
    trail_uuid: string,
    table_id: number,
    unit: string,
    setLoading?: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    if (
      footnoteSoftSaveState.insert.length === 0 &&
      footnoteSoftSaveState.update.length === 0 &&
      footnoteSoftSaveState.delete.length === 0
    ) {
      // if no save needed, refresh data only
      fetchAllFootnoteData(trail_uuid, table_id, unit, null);
      return;
    }

    const inserts = footnoteSoftSaveState.insert.map(
      (item: DPFootnote.PayloadType) => ({
        ...item,
        footnote_id: undefined,
      })
    );
    const updates = footnoteSoftSaveState.update;
    const deletes = footnoteSoftSaveState.delete;

    setLoading && setLoading(true);
    await Promise.allSettled([
      inserts?.length > 0 ? insertFootnoteDetail(inserts) : null,
      updates?.length > 0 ? updateFootnoteDetail(updates) : null,
      deletes?.length > 0 ? deleteFootnoteDetail(deletes) : null,
    ])
      .then(([insertRes, updateRes, deleteRes]) => {
        const insertSuccess =
          insertRes.status === "fulfilled" &&
          insertRes.value?.toLowerCase().includes("success");
        const updateSuccess =
          updateRes.status === "fulfilled" &&
          updateRes.value?.toLowerCase().includes("success");
        const deleteSuccess =
          deleteRes.status === "fulfilled" &&
          deleteRes.value?.toLowerCase().includes("success");

        dispatch(
          updateDPFootnoteSoftSave({
            insert: insertSuccess ? [] : footnoteSoftSaveState.insert,
            update: updateSuccess ? [] : footnoteSoftSaveState.update,
            delete: deleteSuccess ? [] : footnoteSoftSaveState.delete,
          })
        );

        if (insertSuccess || updateSuccess || deleteSuccess) {
          fetchAllFootnoteData(trail_uuid, table_id, unit, null);
        }
      })
      .catch((e) => {
        message.error("Failed to save footnotes.");
      })
      .finally(() => {
        setLoading && setLoading(true);
      });
  };

  /**
   * delete and move visit level and cell level footnotes when add, delete, duplicate the visit
   * @param direction
   * @param operation
   * @param fromVisit
   */

  const moveFootnoteByVisitChange = (
    direction: "left" | "right",
    operation: "add" | "duplicate" | "delete",
    fromVisit: number
  ) => {
    const step = 1;
    const mergedFootnotelist = getFootnoteStateMergedWithSoftSave();
    let tmpFootnoteSoftSaveState = JSON.parse(
      JSON.stringify(footnoteSoftSaveState)
    );
  
    if (operation === "delete") {
      const footnoteIdListToDelete = mergedFootnotelist
        .filter(
          (item: DPFootnote.PayloadType) =>
            (item.footnote_group === FOOTNOTE_GROUP.VISIT ||
              item.footnote_group === FOOTNOTE_GROUP.CELL) &&
            item.visit === fromVisit
        )
        .map((item: DPFootnote.PayloadType) => item.footnote_id);
  
      const insertList = tmpFootnoteSoftSaveState.insert.filter(
        (item: DPFootnote.PayloadType) =>
          !footnoteIdListToDelete.includes(item.footnote_id)
      );
  
      const updateList = tmpFootnoteSoftSaveState.update.filter(
        (item: DPFootnote.PayloadType) =>
          !footnoteIdListToDelete.includes(item.footnote_id)
      );
  
      const deleteList = Array.from(
        new Set([
          ...footnoteIdListToDelete.filter((id) => typeof id === "number"),
          ...tmpFootnoteSoftSaveState.delete,
        ])
      );
  
      tmpFootnoteSoftSaveState = {
        ...tmpFootnoteSoftSaveState,
        insert: insertList,
        update: updateList,
        delete: deleteList,
      };
    }
  
    const footnoteListToMove = mergedFootnotelist.filter(
      (item: DPFootnote.PayloadType) =>
        (item.footnote_group === FOOTNOTE_GROUP.VISIT ||
          item.footnote_group === FOOTNOTE_GROUP.CELL) &&
        item.visit > fromVisit
    );
  
    const footnoteIdListToMove = footnoteListToMove.map(
      (item: DPFootnote.PayloadType) => item.footnote_id
    );
  
    const insertList = tmpFootnoteSoftSaveState.insert.map(
      (item: DPFootnote.PayloadType) => {
        if (footnoteIdListToMove.includes(item.footnote_id)) {
          const newVisit = item.visit
            ? direction === "left"
              ? item.visit - step
              : item.visit + step
            : null;
          return {
            ...item,
            visit: newVisit,
            footnote_preview_text: item.footnote_preview_text.replace(
              new RegExp(`\\bVisit ${item.visit}\\b`, 'g'),
              `Visit ${newVisit}`
            ),
          };
        } else {
          return item;
        }
      }
    );
  
    const updateList = [
      ...footnoteListToMove
        .filter(
          (item: DPFootnote.PayloadType) => typeof item.footnote_id !== "string"
        )
        .map((item: DPFootnote.PayloadType) => {
          const newVisit = item.visit
            ? direction === "left"
              ? item.visit - step
              : item.visit + step
            : null;
          return {
            ...item,
            visit: newVisit,
            footnote_preview_text: item.footnote_preview_text.replace(
              new RegExp(`\\bVisit ${item.visit}\\b`, 'g'),
              `Visit ${newVisit}`
            ),
          };
        }),
      ...tmpFootnoteSoftSaveState.update,
    ];
  
    tmpFootnoteSoftSaveState = {
      ...tmpFootnoteSoftSaveState,
      insert: insertList,
      update: uniqueArray(updateList, "footnote_id"),
    };
  
    if (operation === "duplicate") {
      const footnoteListToCopy = mergedFootnotelist
        .filter(
          (item: DPFootnote.PayloadType) =>
            (item.footnote_group === FOOTNOTE_GROUP.VISIT ||
              item.footnote_group === FOOTNOTE_GROUP.CELL) &&
            item.visit === fromVisit
        )
        .map((item: DPFootnote.PayloadType, index: number) => {
          const newVisit = item.visit ? item.visit + step : null;
          return {
            ...item,
            footnote_id: `${SOFT_SAVE_ID_PREFIX}${new Date().getTime() + index}`,
            trial_uuid: locationState.trial_id,
            table_id: locationState.tableItems.table_id,
            dprot_id: locationState.tableItems.dprot_id,
            visit: newVisit,
            footnote_preview_text: item.footnote_preview_text.replace(
              new RegExp(`\\bVisit ${item.visit}\\b`, 'g'),
              `Visit ${newVisit}`
            ),
            footnote_details_json: item.footnote_details_json.replace(
              new RegExp(`\\bVisit ${item.visit}\\b`, 'g'),
              `Visit ${newVisit}`
            ),
          };
        });
  
      tmpFootnoteSoftSaveState = {
        ...tmpFootnoteSoftSaveState,
        insert: [...footnoteListToCopy, ...tmpFootnoteSoftSaveState.insert],
      };
    }
  
    dispatch(updateDPFootnoteSoftSave(tmpFootnoteSoftSaveState));
  };
  

  /**
   * move visit level and cell level footnotes when move visit
   * @param direction
   * @param fromVisit
   * @param toVisit
   */
  const moveFootnoteByVisitMovement = (
    direction: "left" | "right",
    fromVisit: number,
    toVisit: number,
    periodData: Digital_Protocol_SoA_Table_Data.PeriodData[]
  ) => {
    if (fromVisit === toVisit) {
      return;
    }

    const mergedFootnotelist = getFootnoteStateMergedWithSoftSave();
    let tmpFootnoteSoftSaveState = JSON.parse(
      JSON.stringify(footnoteSoftSaveState)
    );

    // move the footnotes on the moving visit nodes
    const newPeriod = periodData.find((item) => item.visits.includes(toVisit));
    const footnoteListToMove = mergedFootnotelist.filter(
      (item: DPFootnote.PayloadType) =>
        (item.footnote_group === FOOTNOTE_GROUP.VISIT ||
          item.footnote_group === FOOTNOTE_GROUP.CELL) &&
        item.visit === fromVisit
    );
    const insertfootnoteListToMove = footnoteListToMove
      .filter(
        (item: DPFootnote.PayloadType) => typeof item.footnote_id === "string"
      )
      .map((item: DPFootnote.PayloadType) => {
        const [footnote_details_json, footnote_preview_text] =
          convertPreviewOnVisitChange(item, fromVisit, toVisit);
        return {
          ...item,
          visit: toVisit,
          period_id: newPeriod?.period_id || item.period_id,
          footnote_details_json,
          footnote_preview_text,
        };
      });
    const updatafootnoteListToMove = footnoteListToMove
      .filter(
        (item: DPFootnote.PayloadType) => typeof item.footnote_id === "number"
      )
      .map((item: DPFootnote.PayloadType) => {
        const [footnote_details_json, footnote_preview_text] =
          convertPreviewOnVisitChange(item, fromVisit, toVisit);
        return {
          ...item,
          visit: toVisit,
          period_id: newPeriod?.period_id || item.period_id,
          footnote_details_json,
          footnote_preview_text,
        };
      });

    tmpFootnoteSoftSaveState = {
      ...tmpFootnoteSoftSaveState,
      insert: uniqueArray(
        [...insertfootnoteListToMove, ...tmpFootnoteSoftSaveState.insert],
        "footnote_id"
      ),
      update: uniqueArray(
        [...updatafootnoteListToMove, ...tmpFootnoteSoftSaveState.update],
        "footnote_id"
      ),
    };

    // move other necessary footnotes
    let otherFootnoteListToMove = [];
    if (fromVisit < toVisit) {
      otherFootnoteListToMove = mergedFootnotelist.filter(
        (item: DPFootnote.PayloadType) =>
          (item.footnote_group === FOOTNOTE_GROUP.VISIT ||
            item.footnote_group === FOOTNOTE_GROUP.CELL) &&
          item.visit > fromVisit &&
          item.visit <= toVisit
      );
    } else if (fromVisit > toVisit) {
      otherFootnoteListToMove = mergedFootnotelist.filter(
        (item: DPFootnote.PayloadType) =>
          (item.footnote_group === FOOTNOTE_GROUP.VISIT ||
            item.footnote_group === FOOTNOTE_GROUP.CELL) &&
          item.visit >= toVisit &&
          item.visit < fromVisit
      );
    }

    const otherFootnoteIdListToMove = otherFootnoteListToMove.map(
      (item: DPFootnote.PayloadType) => item.footnote_id
    );
    const insertList = tmpFootnoteSoftSaveState.insert.map(
      (item: DPFootnote.PayloadType) => {
        if (otherFootnoteIdListToMove.includes(item.footnote_id)) {
          const isVisit = item.footnote_group === FOOTNOTE_GROUP.VISIT;
          const toV = item.visit
            ? direction === "right"
              ? item.visit - 1
              : item.visit + 1
            : null;
          let jsonData = JSON.parse(item.footnote_details_json);
          let footnote_preview_text = item.footnote_preview_text;
          if (isVisit) {
            jsonData = {
              ...jsonData,
              footnotePreivew: jsonData.footnotePreivew.replaceAll(
                `Visit ${item.visit}`,
                `Visit ${toV}`
              ),
              previewPre: jsonData.previewPre.replaceAll(
                `Visit ${item.visit}`,
                `Visit ${toV}`
              ),
            };
            footnote_preview_text = footnote_preview_text.replaceAll(
              `Visit ${item.visit}`,
              `Visit ${toV}`
            );
          }
          return {
            ...item,
            visit: toV,
            footnote_details_json: JSON.stringify(jsonData),
            footnote_preview_text,
          };
        } else {
          return item;
        }
      }
    );
    const updateList = [
      ...otherFootnoteListToMove
        .filter(
          (item: DPFootnote.PayloadType) => typeof item.footnote_id !== "string"
        )
        .map((item: DPFootnote.PayloadType) => {
          const isVisit = item.footnote_group === FOOTNOTE_GROUP.VISIT;
          const toV = item.visit
            ? direction === "right"
              ? item.visit - 1
              : item.visit + 1
            : null;
          let jsonData = JSON.parse(item.footnote_details_json);
          let footnote_preview_text = item.footnote_preview_text;
          if (isVisit) {
            jsonData = {
              ...jsonData,
              footnotePreivew: jsonData.footnotePreivew.replaceAll(
                `Visit ${item.visit}`,
                `Visit ${toV}`
              ),
              previewPre: jsonData.previewPre.replaceAll(
                `Visit ${item.visit}`,
                `Visit ${toV}`
              ),
            };
            footnote_preview_text = footnote_preview_text.replaceAll(
              `Visit ${item.visit}`,
              `Visit ${toV}`
            );
          }
          return {
            ...item,
            visit: toV,
            footnote_details_json: JSON.stringify(jsonData),
            footnote_preview_text,
          };
        }),
      ...tmpFootnoteSoftSaveState.update,
    ];
    tmpFootnoteSoftSaveState = {
      ...tmpFootnoteSoftSaveState,
      insert: insertList,
      update: uniqueArray(updateList, "footnote_id"),
    };
    dispatch(updateDPFootnoteSoftSave(tmpFootnoteSoftSaveState));
  };

  /**
   * delete and move period, visit level and cell level footnotes when add, delete, duplicate the period
   * @param direction
   * @param needDelete
   * @param fromPeriodId
   * @param period
   */

  const moveFootnoteByPeriodChange = (
    direction: "left" | "right",
    operation: "add" | "duplicate" | "delete",
    fromPeriodId: number,
    period: Digital_Protocol_SoA_Table_Data.PeriodData,
    newPeriodName?: string
  ) => {
    const step = period.visits.length;
    const mergedFootnotelist = getFootnoteStateMergedWithSoftSave();
    let tmpFootnoteSoftSaveState = JSON.parse(
      JSON.stringify(footnoteSoftSaveState)
    );
  
    if (operation === "delete") {
      const footnoteIdListToDelete = mergedFootnotelist
        .filter(
          (item: DPFootnote.PayloadType) =>
            (item.footnote_group === FOOTNOTE_GROUP.PERIOD ||
              item.footnote_group === FOOTNOTE_GROUP.VISIT ||
              item.footnote_group === FOOTNOTE_GROUP.CELL) &&
            item.period_id === fromPeriodId
        )
        .map((item: DPFootnote.PayloadType) => item.footnote_id);
  
      const insertList = tmpFootnoteSoftSaveState.insert.filter(
        (item: DPFootnote.PayloadType) =>
          !footnoteIdListToDelete.includes(item.footnote_id)
      );
  
      const updateList = tmpFootnoteSoftSaveState.update.filter(
        (item: DPFootnote.PayloadType) =>
          !footnoteIdListToDelete.includes(item.footnote_id)
      );
  
      const deleteList = Array.from(
        new Set([
          ...footnoteIdListToDelete.filter((id) => typeof id === "number"),
          ...tmpFootnoteSoftSaveState.delete,
        ])
      );
  
      tmpFootnoteSoftSaveState = {
        ...tmpFootnoteSoftSaveState,
        insert: insertList,
        update: updateList,
        delete: deleteList,
      };
    }
  
    const footnoteListToMove = mergedFootnotelist.filter(
      (item: DPFootnote.PayloadType) =>
        (item.footnote_group === FOOTNOTE_GROUP.PERIOD ||
          item.footnote_group === FOOTNOTE_GROUP.VISIT ||
          item.footnote_group === FOOTNOTE_GROUP.CELL) &&
        item.period_id > fromPeriodId
    );
  
    const footnoteIdListToMove = footnoteListToMove.map(
      (item: DPFootnote.PayloadType) => item.footnote_id
    );
  
    const insertList = tmpFootnoteSoftSaveState.insert.map(
      (item: DPFootnote.PayloadType) => {
        if (footnoteIdListToMove.includes(item.footnote_id)) {
          const newPeriodId =
            direction === "left" ? item.period_id - 1 : item.period_id + 1;
          const newVisit = item.visit
            ? direction === "left"
              ? item.visit - step
              : item.visit + step
            : item.visit;
  
          return {
            ...item,
            period_id: newPeriodId,
            visit: newVisit,
            footnote_preview_text: item.footnote_preview_text.replace(
              new RegExp(`\\bVisit ${item.visit}\\b`, 'g'),
              `Visit ${newVisit}`
            ),
          };
        } else {
          return item;
        }
      }
    );
  
    const updateList = [
      ...footnoteListToMove
        .filter(
          (item: DPFootnote.PayloadType) => typeof item.footnote_id !== "string"
        )
        .map((item: DPFootnote.PayloadType) => {
          const newPeriodId =
            direction === "left" ? item.period_id - 1 : item.period_id + 1;
          const newVisit = item.visit
            ? direction === "left"
              ? item.visit - step
              : item.visit + step
            : item.visit;
  
          return {
            ...item,
            period_id: newPeriodId,
            visit: newVisit,
            footnote_preview_text: item.footnote_preview_text.replace(
              new RegExp(`\\bVisit ${item.visit}\\b`, 'g'),
              `Visit ${newVisit}`
            ),
          };
        }),
      ...tmpFootnoteSoftSaveState.update,
    ];
  
    tmpFootnoteSoftSaveState = {
      ...tmpFootnoteSoftSaveState,
      insert: insertList,
      update: uniqueArray(updateList, "footnote_id"),
    };
  
    if (operation === "duplicate") {
      const footnoteListToCopy = mergedFootnotelist
        .filter(
          (item: DPFootnote.PayloadType) =>
            (item.footnote_group === FOOTNOTE_GROUP.PERIOD ||
              item.footnote_group === FOOTNOTE_GROUP.VISIT ||
              item.footnote_group === FOOTNOTE_GROUP.CELL) &&
            item.period_id === fromPeriodId
        )
        .map((item: DPFootnote.PayloadType, index: number) => {
          const newVisit = item.visit ? item.visit + step : null;
          return {
            ...item,
            footnote_id: `${SOFT_SAVE_ID_PREFIX}${new Date().getTime() + index}`,
            trial_uuid: locationState.trial_id,
            table_id: locationState.tableItems.table_id,
            dprot_id: locationState.tableItems.dprot_id,
            period_id: item.period_id + 1,
            visit: newVisit,
            footnote_preview_text:
              item.footnote_group === FOOTNOTE_GROUP.VISIT
                ? item.footnote_preview_text.replace(
                    new RegExp(`\\bVisit ${item.visit}\\b`, 'g'),
                    `Visit ${newVisit}`
                  )
                : item.footnote_group === FOOTNOTE_GROUP.PERIOD
                ? item.footnote_preview_text.replace(
                    period.period_name,
                    newPeriodName
                  )
                : item.footnote_preview_text,
            footnote_details_json:
              item.footnote_group === FOOTNOTE_GROUP.VISIT
                ? item.footnote_details_json.replace(
                    new RegExp(`\\bVisit ${item.visit}\\b`, 'g'),
                    `Visit ${newVisit}`
                  )
                : item.footnote_group === FOOTNOTE_GROUP.PERIOD
                ? item.footnote_details_json.replace(
                    period.period_name,
                    newPeriodName
                  )
                : item.footnote_details_json,
          };
        });
  
      tmpFootnoteSoftSaveState = {
        ...tmpFootnoteSoftSaveState,
        insert: [...footnoteListToCopy, ...tmpFootnoteSoftSaveState.insert],
      };
    }
  
    dispatch(updateDPFootnoteSoftSave(tmpFootnoteSoftSaveState));
  };

  /**
   * move period level, visit level and cell level footnotes when move period
   * @param direction
   * @param fromPeriod
   * @param toPeriod
   * @param periodDataBeforeMove
   */
  const moveFootnoteByPeriodMovement = (
    direction: "left" | "right",
    fromPeriod: number,
    toPeriod: number,
    periodDataBeforeMove: Digital_Protocol_SoA_Table_Data.PeriodData[]
  ) => {
    if (fromPeriod === toPeriod) {
      return;
    }

    const mergedFootnotelist = getFootnoteStateMergedWithSoftSave();
    let tmpFootnoteSoftSaveState = JSON.parse(
      JSON.stringify(footnoteSoftSaveState)
    );

    // move the footnotes on the moving period nodes
    const footnoteListToMove = mergedFootnotelist.filter(
      (item: DPFootnote.PayloadType) =>
        (item.footnote_group === FOOTNOTE_GROUP.PERIOD ||
          item.footnote_group === FOOTNOTE_GROUP.VISIT ||
          item.footnote_group === FOOTNOTE_GROUP.CELL) &&
        item.period_id === fromPeriod
    );
    // count the total number of visit
    const periodDataFiltered =
      fromPeriod < toPeriod
        ? periodDataBeforeMove.filter(
            (item) => item.period_id >= fromPeriod && item.period_id <= toPeriod
          )
        : periodDataBeforeMove.filter(
            (item) => item.period_id >= toPeriod && item.period_id <= fromPeriod
          );
    let step = periodDataFiltered.reduce(
      (accumulator, currentValue) => accumulator + currentValue.no_of_visits,
      0
    );
    step = step - periodDataBeforeMove[fromPeriod - 1].no_of_visits;
    const insertfootnoteListToMove = footnoteListToMove
      .filter(
        (item: DPFootnote.PayloadType) => typeof item.footnote_id === "string"
      )
      .map((item: DPFootnote.PayloadType) => {
        const fromVisit = item.visit || null;
        const toVisit = fromVisit
          ? direction === "right"
            ? fromVisit + step
            : fromVisit - step
          : null;
        const [footnote_details_json, footnote_preview_text] =
          convertPreviewOnVisitChange(item, fromVisit, toVisit);
        return {
          ...item,
          visit: toVisit,
          period_id: toPeriod || item.period_id,
          footnote_details_json,
          footnote_preview_text,
        };
      });
    const updatafootnoteListToMove = footnoteListToMove
      .filter(
        (item: DPFootnote.PayloadType) => typeof item.footnote_id === "number"
      )
      .map((item: DPFootnote.PayloadType) => {
        const fromVisit = item.visit || null;
        const toVisit = fromVisit
          ? direction === "right"
            ? fromVisit + step
            : fromVisit - step
          : null;
        const [footnote_details_json, footnote_preview_text] =
          convertPreviewOnVisitChange(item, fromVisit, toVisit);
        return {
          ...item,
          visit: toVisit,
          period_id: toPeriod || item.period_id,
          footnote_details_json,
          footnote_preview_text,
        };
      });

    tmpFootnoteSoftSaveState = {
      ...tmpFootnoteSoftSaveState,
      insert: uniqueArray(
        [...insertfootnoteListToMove, ...tmpFootnoteSoftSaveState.insert],
        "footnote_id"
      ),
      update: uniqueArray(
        [...updatafootnoteListToMove, ...tmpFootnoteSoftSaveState.update],
        "footnote_id"
      ),
    };

    // move other necessary footnotes
    let otherFootnoteListToMove = [];
    if (fromPeriod < toPeriod) {
      otherFootnoteListToMove = mergedFootnotelist.filter(
        (item: DPFootnote.PayloadType) =>
          (item.footnote_group === FOOTNOTE_GROUP.PERIOD ||
            item.footnote_group === FOOTNOTE_GROUP.VISIT ||
            item.footnote_group === FOOTNOTE_GROUP.CELL) &&
          item.period_id > fromPeriod &&
          item.period_id <= toPeriod
      );
    } else if (fromPeriod > toPeriod) {
      otherFootnoteListToMove = mergedFootnotelist.filter(
        (item: DPFootnote.PayloadType) =>
          (item.footnote_group === FOOTNOTE_GROUP.PERIOD ||
            item.footnote_group === FOOTNOTE_GROUP.VISIT ||
            item.footnote_group === FOOTNOTE_GROUP.CELL) &&
          item.period_id >= toPeriod &&
          item.period_id < fromPeriod
      );
    }

    // move other necessary footnotes
    const otherFootnoteIdListToMove = otherFootnoteListToMove.map(
      (item: DPFootnote.PayloadType) => item.footnote_id
    );
    step = periodDataBeforeMove[fromPeriod - 1].no_of_visits;
    const insertList = tmpFootnoteSoftSaveState.insert.map(
      (item: DPFootnote.PayloadType) => {
        if (otherFootnoteIdListToMove.includes(item.footnote_id)) {
          const fromVisit = item.visit || null;
          const toVisit = fromVisit
            ? direction === "right"
              ? fromVisit - step
              : fromVisit + step
            : null;
          const [footnote_details_json, footnote_preview_text] =
            convertPreviewOnVisitChange(item, fromVisit, toVisit);
          return {
            ...item,
            period_id:
              direction === "right" ? item.period_id - 1 : item.period_id + 1,
            visit: toVisit,
            footnote_details_json,
            footnote_preview_text,
          };
        } else {
          return item;
        }
      }
    );

    const updateList = [
      ...otherFootnoteListToMove
        .filter(
          (item: DPFootnote.PayloadType) => typeof item.footnote_id !== "string"
        )
        .map((item: DPFootnote.PayloadType) => {
          const fromVisit = item.visit || null;
          const toVisit = fromVisit
            ? direction === "right"
              ? fromVisit - step
              : fromVisit + step
            : null;
          const [footnote_details_json, footnote_preview_text] =
            convertPreviewOnVisitChange(item, fromVisit, toVisit);
          return {
            ...item,
            period_id:
              direction === "right" ? item.period_id - 1 : item.period_id + 1,
            visit: toVisit,
            footnote_details_json,
            footnote_preview_text,
          };
        }),
      ...tmpFootnoteSoftSaveState.update,
    ];
    tmpFootnoteSoftSaveState = {
      ...tmpFootnoteSoftSaveState,
      insert: insertList,
      update: uniqueArray(updateList, "footnote_id"),
    };
    dispatch(updateDPFootnoteSoftSave(tmpFootnoteSoftSaveState));
  };

  /**
   * update perview text for Preiod or Visit
   * @param originText
   * @param newText
   * @param footnoteGroup
   * @param periodId
   * @param visit
   */
  const updatePreviewByPeriodVisit = (
    originText: string,
    newText: string,
    footnoteGroup: string,
    periodId: number,
    visit?: number
  ) => {
    const mergedFootnotelist: DPFootnote.PayloadType[] =
      getFootnoteStateMergedWithSoftSave();
    const itemsToChange = mergedFootnotelist
      .filter((item: DPFootnote.PayloadType) => {
        if (visit) {
          return (
            item.footnote_group === footnoteGroup &&
            item.period_id === periodId &&
            item.visit === visit
          );
        } else {
          return (
            item.footnote_group === footnoteGroup && item.period_id === periodId
          );
        }
      })
      .filter(
        (item: DPFootnote.PayloadType) =>
          !footnoteSoftSaveState.delete.includes(item.footnote_id)
      )
      .filter((item: DPFootnote.PayloadType) => {
        return item.footnote_preview_text.includes(originText);
      })
      .map((item: DPFootnote.PayloadType) => {
        const footnote_preview_text = item.footnote_preview_text.replaceAll(
          originText,
          newText
        );
        let jsonData = JSON.parse(item.footnote_details_json);
        jsonData = {
          ...jsonData,
          footnotePreivew: jsonData.footnotePreivew.replaceAll(
            originText,
            newText
          ),
          previewPre: jsonData.previewPre.replaceAll(originText, newText),
        };
        return {
          ...item,
          footnote_details_json: JSON.stringify(jsonData),
          footnote_preview_text,
        };
      });
    const insertList = uniqueArray(
      [
        ...itemsToChange.filter(
          (item: DPFootnote.PayloadType) => typeof item.footnote_id === "string"
        ),
        ...footnoteSoftSaveState.insert,
      ],
      "footnote_id"
    );
    const updateList = uniqueArray(
      [
        ...itemsToChange.filter(
          (item: DPFootnote.PayloadType) => typeof item.footnote_id === "number"
        ),
        ...footnoteSoftSaveState.update,
      ],
      "footnote_id"
    );
    dispatch(
      updateDPFootnoteSoftSave({
        ...footnoteSoftSaveState,
        insert: insertList,
        update: updateList,
      })
    );
  };

  /**
   * When user deleted the period/visit and that deleted period/visit is in the footnotes, a warning should be marked
   * @param soaTableData
   * @param footnoteDataList
   * @returns
   */
  const doValidationForWarning = (
    soaTableData: Digital_Protocol_SoA_Table_Data.DP_Scenario | null,
    footnoteDataList: DPFootnote.FootnoteData[]
  ) => {
    try {
      const soaVisits =
        soaTableData?.scenarios?.length > 0
          ? soaTableData.scenarios[0]["Schedule of Activities"].visits
          : dpSoaState.soaVisits;
      const maxVisit = Math.max(...soaVisits);
      return footnoteDataList.map((item) => {
        const jsonData = JSON.parse(item.footnote_details_json);
        /* 
          Comparing the mapping of visits between SoA builder table and footnotes to check if the visit number is not in the SoA builder table.
          Check scope: footnote type "Activity is continuously monitored" and "Activity has a different workflow for certain patients"
         */
        if (
          jsonData?.day_week_visit_type === "Visit" &&
          jsonData?.continuously_monitored_to_number_text > maxVisit
        ) {
          // type: Activity is continuously monitored
          const textToHighlight = `Visit ${jsonData?.continuously_monitored_from_number_text} to Visit ${jsonData?.continuously_monitored_to_number_text}`;
          return {
            ...item,
            warn: {
              visit: true,
            },
            footnote_preview_text: item.footnote_preview_text.replaceAll(
              textToHighlight,
              `<span class="highlight"> ${textToHighlight} </span>`
            ),
          };
        } else if (jsonData?.different_workflow_1_select > maxVisit) {
          // type: Activity has a different workflow for certain patients
          const textToHighlight = `${jsonData?.different_workflow_1_select} visit`;
          return {
            ...item,
            warn: {
              visit: true,
            },
            footnote_preview_text: item.footnote_preview_text.replaceAll(
              textToHighlight,
              `<span class="highlight"> ${textToHighlight} </span>`
            ),
          };
        } else {
          return item;
        }
      });
    } catch (error) {}
  };

  /**
   * check if any "warn" property exist in the list, return true if any warning there
   * @param footnoteList
   * @returns
   */
  const checkIfExistFootnoteWarning = (
    footnoteList: DPFootnote.FootnoteData[]
  ) => {
    if (!footnoteList || footnoteList?.length === 0) {
      return false;
    }
    return footnoteList.some((item) => item.warn);
  };

  const convertPreviewOnVisitChange = (
    itemData: DPFootnote.PayloadType,
    fromVisit: null | number,
    toVisit: null | number
  ) => {
    if (!fromVisit) {
      return [itemData.footnote_details_json, itemData.footnote_preview_text];
    }

    let jsonData = JSON.parse(itemData.footnote_details_json);
    jsonData = {
      ...jsonData,
      footnotePreivew: jsonData.footnotePreivew.replaceAll(
        `Visit ${fromVisit}`,
        `Visit ${toVisit}`
      ),
      previewPre: jsonData.previewPre.replaceAll(
        `Visit ${fromVisit}`,
        `Visit ${toVisit}`
      ),
    };
    const footnote_preview_text = itemData.footnote_preview_text.replaceAll(
      `Visit ${fromVisit}`,
      `Visit ${toVisit}`
    );

    return [JSON.stringify(jsonData), footnote_preview_text];
  };

  return {
    fetchFootnoteConfig,
    fetchAllFootnoteData,
    filterDropdownOptionsByFootnoteType,
    getFootnoteStateMergedWithSoftSave,
    doFootnoteSoftDelete,
    doFootnoteSoftUpsert,
    doFootnoteHardSave,
    moveFootnoteByVisitChange,
    moveFootnoteByVisitMovement,
    moveFootnoteByPeriodChange,
    moveFootnoteByPeriodMovement,
    updatePreviewByPeriodVisit,
    checkIfExistFootnoteWarning,
  };
}
